import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Users, Inbox, FileSearch, CheckSquare, FolderLock, FileText, BrainCircuit, Table2, ArrowRight, Play, Mail, Lock, Menu, X } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const Home = () => {
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    role: '',
    email: '',
    phone: '',
    message: ''
  });

  const [authMode, setAuthMode] = useState('login');
  const [authForm, setAuthForm] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...contactForm,
          recaptchaToken: 'frontend-submission'
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Message Sent Successfully!",
          description: data.message,
        });
        setContactForm({
          firstName: '',
          lastName: '',
          companyName: '',
          role: '',
          email: '',
          phone: '',
          message: ''
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to send message. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again later.",
        variant: "destructive"
      });
    }
  };

  const handleAuthSubmit = (e) => {
    e.preventDefault();
    if (authMode === 'signup' && authForm.password !== authForm.confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        variant: "destructive"
      });
      return;
    }
    toast({
      title: authMode === 'login' ? "Welcome Back!" : "Account Created!",
      description: authMode === 'login' ? "You've been logged in successfully." : "Your account is ready to use.",
    });
    setAuthForm({
      username: '',
      email: '',
      password: '',
      confirmPassword: ''
    });
  };

  const features = [
    {
      icon: <Users className="w-8 h-8" />,
      title: "Seamless Team Collaboration",
      description: "Invite your team to collaborate effortlessly within dedicated Amigo groups. Watch productivity and teamwork flourish in a streamlined and intuitive workspace."
    },
    {
      icon: <Inbox className="w-8 h-8" />,
      title: "Dedicated Inboxes",
      description: "Each Amigo comes with its own inbox to keep team messages and updates. Manage everything from invoices to project updates without losing track."
    },
    {
      icon: <FileSearch className="w-8 h-8" />,
      title: "Email Document Querying",
      description: "Let Amigos summarise attachments and documents in seconds. Get the clarity you need, when you need it—no hassle, no delays."
    },
    {
      icon: <CheckSquare className="w-8 h-8" />,
      title: "Effortless Task Management",
      description: "Convert emails into actionable tasks in a few clicks. Assign and track progress seamlessly within the platform."
    },
    {
      icon: <FolderLock className="w-8 h-8" />,
      title: "Secure File Storage",
      description: "Every Amigo features a secure drive for storing documents, images, and projects. Share files easily with team members, keeping everything accessible and organised."
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "AI Document Tools",
      description: "Group chat and email attachments into structured documents. Create summary reports, contracts, templates, and more with just a few clicks."
    },
    {
      icon: <BrainCircuit className="w-8 h-8" />,
      title: "Smart Document Processing",
      description: "Set instructions in natural language to extract key information from attached documents and organise it into tables with ease. Set up custom fields in your inbox for smarter workflows."
    },
    {
      icon: <Table2 className="w-8 h-8" />,
      title: "Data Tables",
      description: "Manage aggregated data like invoices, resumes, insurance policies, or code snippets with powerful data tables. Edit, save, and export data effortlessly, simplifying your workflow."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrollY > 50 ? 'bg-white shadow-lg' : 'bg-black/90 backdrop-blur-lg'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/assets/images/sayamigo-logo.png" alt="SayAmigo" className="h-8 sm:h-10 w-auto" />
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              <a href="#features" className={`transition-colors font-medium ${scrollY > 50 ? 'text-gray-700 hover:text-cyan-600' : 'text-white hover:text-cyan-400'}`}>Features</a>
              <a href="#about" className={`transition-colors font-medium ${scrollY > 50 ? 'text-gray-700 hover:text-cyan-600' : 'text-white hover:text-cyan-400'}`}>About</a>
              <a href="#contact" className={`transition-colors font-medium ${scrollY > 50 ? 'text-gray-700 hover:text-cyan-600' : 'text-white hover:text-cyan-400'}`}>Contact</a>
              <Button 
                variant="default" 
                className="bg-gradient-to-r from-cyan-400 to-blue-500 hover:opacity-90 text-white shadow-lg hover:shadow-xl transition-all duration-300"
                onClick={() => setAuthMode('signup')}
              >
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className={`w-6 h-6 ${scrollY > 50 ? 'text-gray-700' : 'text-white'}`} />
              ) : (
                <Menu className={`w-6 h-6 ${scrollY > 50 ? 'text-gray-700' : 'text-white'}`} />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-700/20">
              <div className="flex flex-col gap-4 pt-4">
                <a 
                  href="#features" 
                  className={`transition-colors font-medium ${scrollY > 50 ? 'text-gray-700 hover:text-cyan-600' : 'text-white hover:text-cyan-400'}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Features
                </a>
                <a 
                  href="#about" 
                  className={`transition-colors font-medium ${scrollY > 50 ? 'text-gray-700 hover:text-cyan-600' : 'text-white hover:text-cyan-400'}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  About
                </a>
                <a 
                  href="#contact" 
                  className={`transition-colors font-medium ${scrollY > 50 ? 'text-gray-700 hover:text-cyan-600' : 'text-white hover:text-cyan-400'}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Contact
                </a>
                <Button 
                  variant="default" 
                  className="bg-gradient-to-r from-cyan-400 to-blue-500 hover:opacity-90 text-white w-full"
                  onClick={() => {
                    setAuthMode('signup');
                    setMobileMenuOpen(false);
                  }}
                >
                  Get Started
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 pb-12 sm:pb-20 px-4 sm:px-6 overflow-hidden bg-black min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto w-full relative">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Side - Image */}
            <div className="flex items-center justify-center order-2 lg:order-1">
              <img 
                src="/assets/images/logo.png" 
                alt="SayAmigo" 
                className="w-full max-w-md lg:max-w-lg h-auto"
              />
            </div>

            {/* Right Side - Content */}
            <div className="text-white order-1 lg:order-2">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black uppercase mb-6 sm:mb-8 tracking-tight">
                Say Amigo
              </h1>
              
              <h2 className="text-lg sm:text-xl md:text-2xl font-medium mb-6 sm:mb-8 leading-relaxed">
                Take Control Over Your Invoices – From Collection to Data Extraction
              </h2>

              <div className="space-y-4 sm:space-y-5 mb-8 sm:mb-10 text-sm sm:text-base leading-relaxed tracking-wide">
                <p>1. Spin up your AI-powered agents (we call them Amigos) in just 2-5 minutes.</p>
                <p>2. They can extract data from PDFs, JPEGs, PNGs, even scanned bill copies — no templates or sample invoices required.</p>
                <p>3. Train your Amigos using simple natural language — no coding or complex setups.</p>
              </div>

              <p className="text-lg sm:text-xl font-medium mb-6 sm:mb-8">
                Curious to see Amigos in action?
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-cyan-400 via-teal-400 to-blue-500 hover:opacity-90 text-white px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base rounded-md shadow-lg transition-all duration-300 w-full sm:w-auto"
                  onClick={() => window.scrollTo({ top: document.getElementById('about').offsetTop - 80, behavior: 'smooth' })}
                >
                  PRODUCT VIDEO
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-white/35 hover:border-white/75 hover:bg-transparent text-white px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base rounded-md transition-all duration-300 w-full sm:w-auto"
                  onClick={() => window.scrollTo({ top: document.getElementById('contact').offsetTop - 80, behavior: 'smooth' })}
                >
                  GET IN TOUCH
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 sm:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Product Features
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              SayAmigo is your ultimate workspace, designed to transform teamwork, collaboration, and productivity. 
              With innovative features and AI-powered digital assistants—your Amigos—we simplify your workday 
              and supercharge your team's efficiency.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="group border border-gray-200 hover:border-cyan-400 hover:shadow-lg transition-all duration-300 bg-white"
              >
                <CardHeader className="pb-3">
                  <div className="inline-flex p-3 rounded-lg bg-gray-50 group-hover:bg-cyan-50 text-gray-700 group-hover:text-cyan-600 mb-3 transition-all duration-300 w-fit">
                    {feature.icon}
                  </div>
                  <CardTitle className="text-lg text-gray-900 font-semibold">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm text-gray-600 leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 sm:py-24 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              About SayAmigo
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
              At SayAmigo, our mission is to empower businesses with a unified platform that transforms collaboration, 
              enhances efficiency, and simplifies workflows. Inspired by the best features of leading collaboration tools, 
              SayAmigo seamlessly integrates them into a single, easy-to-use app designed to elevate teamwork and performance. 
              Driven by a vision to make modern workplaces more productive and connected, we are dedicated to creating solutions 
              that help teams thrive in an ever-evolving world of work.
            </p>
          </div>

          <div className="relative rounded-xl overflow-hidden shadow-xl bg-black border border-gray-200">
            <div className="aspect-video">
              <iframe 
                src="https://player.vimeo.com/video/1035046178?badge=0&autopause=0&player_id=0&app_id=58479" 
                className="w-full h-full"
                frameBorder="0" 
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write" 
                allowFullScreen
                title="SayAmigo Product Demo"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact & Auth Section */}
      <section id="contact" className="py-16 sm:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border border-gray-200 shadow-md">
                <CardHeader className="pb-6 border-b border-gray-100">
                  <CardTitle className="text-3xl text-gray-900">Get In Touch</CardTitle>
                  <CardDescription className="text-base text-gray-600 mt-2">
                    Ready to revolutionise the way your team collaborates? Discover how SayAmigo can transform your workflow 
                    and elevate your productivity. Contact us today or book a demo to meet your Amigos in action!
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <form onSubmit={handleContactSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700">First Name *</label>
                        <Input 
                          value={contactForm.firstName}
                          onChange={(e) => setContactForm({...contactForm, firstName: e.target.value})}
                          required
                          className="border-gray-300 focus:border-cyan-500 focus:ring-cyan-500 h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700">Last Name *</label>
                        <Input 
                          value={contactForm.lastName}
                          onChange={(e) => setContactForm({...contactForm, lastName: e.target.value})}
                          required
                          className="border-gray-300 focus:border-cyan-500 focus:ring-cyan-500 h-11"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Company Name</label>
                      <Input 
                        value={contactForm.companyName}
                        onChange={(e) => setContactForm({...contactForm, companyName: e.target.value})}
                        className="border-gray-300 focus:border-cyan-500 focus:ring-cyan-500 h-11"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Your Role</label>
                      <Input 
                        value={contactForm.role}
                        onChange={(e) => setContactForm({...contactForm, role: e.target.value})}
                        className="border-gray-300 focus:border-cyan-500 focus:ring-cyan-500 h-11"
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700">Email *</label>
                        <Input 
                          type="email"
                          value={contactForm.email}
                          onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                          required
                          className="border-gray-300 focus:border-cyan-500 focus:ring-cyan-500 h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700">Phone No.</label>
                        <Input 
                          type="tel"
                          value={contactForm.phone}
                          onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                          className="border-gray-300 focus:border-cyan-500 focus:ring-cyan-500 h-11"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Message</label>
                      <Textarea 
                        value={contactForm.message}
                        onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                        rows={5}
                        className="border-gray-300 focus:border-cyan-500 focus:ring-cyan-500 resize-none"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-cyan-600 hover:bg-cyan-700 text-white h-12 text-base font-semibold shadow-sm hover:shadow-md transition-all duration-300"
                    >
                      Send Message
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Auth Forms */}
            <div>
              <Card className="border border-gray-200 shadow-md sticky top-24">
                <CardHeader className="pb-4 border-b border-gray-100">
                  <CardTitle className="text-2xl text-gray-900 flex items-center gap-2">
                    <Lock className="w-5 h-5 text-cyan-600" />
                    {authMode === 'signup' ? 'Create Account' : 'Welcome Back'}
                  </CardTitle>
                  <CardDescription className="text-sm">
                    {authMode === 'signup' ? 'Start your journey with SayAmigo' : 'Login to continue'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <form onSubmit={handleAuthSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Username *</label>
                      <Input 
                        value={authForm.username}
                        onChange={(e) => setAuthForm({...authForm, username: e.target.value})}
                        required
                        className="border-gray-300 focus:border-cyan-500 focus:ring-cyan-500 h-10"
                      />
                    </div>
                    {authMode === 'signup' && (
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700">Email Address *</label>
                        <Input 
                          type="email"
                          value={authForm.email}
                          onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
                          required
                          className="border-gray-300 focus:border-cyan-500 focus:ring-cyan-500 h-10"
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-700">Password *</label>
                      <Input 
                        type="password"
                        value={authForm.password}
                        onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
                        required
                        className="border-gray-300 focus:border-cyan-500 focus:ring-cyan-500 h-10"
                      />
                    </div>
                    {authMode === 'signup' && (
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700">Confirm Password *</label>
                        <Input 
                          type="password"
                          value={authForm.confirmPassword}
                          onChange={(e) => setAuthForm({...authForm, confirmPassword: e.target.value})}
                          required
                          className="border-gray-300 focus:border-cyan-500 focus:ring-cyan-500 h-10"
                        />
                      </div>
                    )}
                    {authMode === 'login' && (
                      <div className="text-right">
                        <a href="#" className="text-sm text-cyan-600 hover:text-cyan-700 font-medium">Forgot Password?</a>
                      </div>
                    )}
                    <Button 
                      type="submit" 
                      className="w-full bg-cyan-600 hover:bg-cyan-700 text-white h-11 font-semibold shadow-sm hover:shadow-md transition-all duration-300"
                    >
                      {authMode === 'signup' ? 'Create Account' : 'Log In'}
                    </Button>
                    <div className="text-center pt-2">
                      <button
                        type="button"
                        onClick={() => {
                          setAuthMode(authMode === 'login' ? 'signup' : 'login');
                          setAuthForm({ username: '', email: '', password: '', confirmPassword: '' });
                        }}
                        className="text-sm text-gray-600 hover:text-cyan-600 font-medium transition-colors"
                      >
                        {authMode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
                      </button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="mb-6">
              <img src="/assets/images/sayamigo-logo.png" alt="SayAmigo" className="h-12 w-auto mx-auto mb-4" />
              <p className="text-gray-400 text-base">Empowering teams with AI-powered collaboration</p>
            </div>
            <div className="border-t border-gray-800 pt-6 w-full">
              <p className="text-gray-500 text-sm">&copy; 2025 SayAmigo.com. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;